import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Grid,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import axios from 'axios';

const StyledChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

const Org1DriverEtaDecisionDialog = ({ open, onClose, job, onDecision, loading }) => {
  const [localLoading, setLocalLoading] = useState(false);
  
  // Debug logging when component renders with job data
  React.useEffect(() => {
    if (job && open) {
      console.log('[Org1DriverEtaDecisionDialog] Opened with job:', {
        id: job._id,
        title: job.title,
        status: job.status,
        driver: job.driver,
        driverId: job.driverId,
        pendingOrg1DriverETA: job.pendingOrg1DriverETA,
        submittedOrg1DriverETABy: job.submittedOrg1DriverETABy,
        autoDispatch: job.automatedDispatchEnabled
      });
    }
  }, [job, open]);
  
  // Handle when there is no job or dialog is closed
  if (!job || !open) return null;
  
  // Extract relevant job details
  const {
    _id: jobId,
    title,
    service,
    pendingOrg1DriverETA,
    serviceLocation,
    driver
  } = job;
  
  console.log(`[Org1DriverEtaDecisionDialog] Rendering dialog for job ${jobId} with pendingETA: ${pendingOrg1DriverETA} minutes`);

  // Function to handle ETA decision
  const handleDecision = async (decision) => {
    try {
      console.log(`[Org1DriverEtaDecisionDialog] Processing ${decision} decision for job ${jobId} with ETA ${pendingOrg1DriverETA}`);
      console.log(`[Org1DriverEtaDecisionDialog] Auto-dispatch enabled: ${job.automatedDispatchEnabled}`);
      setLocalLoading(true);
      
      console.log(`[Org1DriverEtaDecisionDialog] Sending API request to /api/jobs/${jobId}/org1-driver-eta-decision with decision=${decision}`);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/jobs/${jobId}/org1-driver-eta-decision`, {
        decision: decision // 'accept' or 'reject'
      });
      
      if (response.data) {
        console.log(`[Org1DriverEtaDecisionDialog] Updated job details:`, {
          status: response.data.job.status,
          driver: response.data.job.driver,
          driverId: response.data.job.driverId,
          eta: response.data.job.eta
        });
        // Call the callback with the updated job and decision type
        onDecision(response.data.job, decision);
      }
    } catch (error) {
      console.error(`[Org1DriverEtaDecisionDialog] ERROR ${decision.toUpperCase()}:`, error);
      console.error(`[Org1DriverEtaDecisionDialog] Error details:`, {
        message: error.message,
        status: error.response?.status,
        responseData: error.response?.data
      });
      // Pass the error to the callback to handle notifications
      const errorMessage = error.response?.data?.message || error.message || `Error ${decision}ing Org1 driver ETA`;
      onDecision(null, decision, errorMessage);
    } finally {
      console.log(`[Org1DriverEtaDecisionDialog] Decision operation complete, closing dialog`);
      setLocalLoading(false);
      onClose();
    }
  };

  // Can only proceed if we have a valid pendingOrg1DriverETA
  if (!pendingOrg1DriverETA) {
    console.log(`[Org1DriverEtaDecisionDialog] ERROR: No pending ETA found for job ${jobId}`);
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>ETA Decision Error</DialogTitle>
        <DialogContent>
          <DialogContentText>
            No pending ETA found for this job. The ETA may have already been processed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Org 1 User ETA Approval
        </Typography>
        <Typography variant="subtitle2" color="textSecondary">
          {job.po} - {title}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={2}>
          {/* Job and ETA details */}
          <Grid item xs={12} md={6}>
            <Box mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Job Details
              </Typography>
              <Divider sx={{ my: 1 }} />
              
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" my={1}>
                    <BuildIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      <strong>Service:</strong> {service}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" my={1}>
                    <LocationOnIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      <strong>Location:</strong> {serviceLocation?.street}, {serviceLocation?.city}, {serviceLocation?.state} {serviceLocation?.zip}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" my={1}>
                    <PersonIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body2">
                      <strong>Driver:</strong> {driver}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          
          {/* ETA Information */}
          <Grid item xs={12} md={6}>
            <Box mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Estimated Time of Arrival
              </Typography>
              <Divider sx={{ my: 1 }} />
              
              <Box display="flex" alignItems="center" justifyContent="center" my={4}>
                <AccessTimeIcon color="error" sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h3" color="error.main">
                  {pendingOrg1DriverETA} <Typography variant="h6" component="span">minutes</Typography>
                </Typography>
              </Box>
              
              <Box textAlign="center" mt={2}>
                <Typography variant="body2" color="textSecondary">
                  The Org 1 user has estimated they will arrive at the service location in the time shown above.
                  <br />
                  Please accept or reject this ETA.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
        
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            <strong>Note:</strong> Accepting this ETA will set the job status to 'Dispatched' with this user assigned.
            {!job.automatedDispatchEnabled &&
              " Rejecting this ETA will clear the user assignment and set the job back to 'Pending'."}
            {job.automatedDispatchEnabled &&
              " Since auto-dispatch is enabled, rejecting this ETA will NOT clear the user assignment."}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Button
          onClick={() => {
            console.log(`[Org1DriverEtaDecisionDialog] Reject button clicked for job ${jobId}`);
            handleDecision('reject');
          }}
          variant="outlined"
          color="error"
          disabled={loading || localLoading}
        >
          Reject ETA
        </Button>
        <Button
          onClick={() => {
            console.log(`[Org1DriverEtaDecisionDialog] Accept button clicked for job ${jobId}`);
            handleDecision('accept');
          }}
          variant="contained"
          color="success"
          disabled={loading || localLoading}
        >
          Accept ETA & Dispatch Job
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Org1DriverEtaDecisionDialog;
